// src/hooks/useClarityTracking.ts
// Hook for tracking user sessions and events with Microsoft Clarity

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import clarityService from '../services/clarityService';
import useAuth from '../features/auth/hooks/useAuth';

/**
 * Custom hook to automatically track user sessions with Clarity
 * - Identifies logged-in users (same id the desktop app reports, so a
 *   portal session and an app session of one person can be joined)
 * - Sets custom tags (account type, signed-in state)
 * - Updates the page id on route change
 */
export const useClarityTracking = () => {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  const identifiedRef = useRef<string | null>(null);

  // Identify user when they log in (once per user, not on every route change)
  useEffect(() => {
    if (!clarityService.isInitialized()) return;
    if (isAuthenticated && user) {
      if (identifiedRef.current !== user.id) {
        identifiedRef.current = user.id;
        clarityService.identify(user.id, undefined, location.pathname, user.email);
        clarityService.setTag('accountType', user.accountType || 'Free Plan');
      }
      clarityService.setTag('signedIn', 'yes');
    } else {
      identifiedRef.current = null;
      clarityService.setTag('signedIn', 'no');
    }
  }, [isAuthenticated, user, location.pathname]);

  // Track page views: update the page id on route change for signed-in users
  useEffect(() => {
    if (clarityService.isInitialized() && isAuthenticated && user) {
      clarityService.identify(user.id, undefined, location.pathname);
    }
  }, [location.pathname, isAuthenticated, user]);

  return clarityService;
};

export default useClarityTracking;
